import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import { useDebounce } from 'uikit';

import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { uerIsUniqueContractObjectNameGql } from '../../workOrderQueries';

interface IUseUerIsUniqueContractObjectName {
    contractId: number | undefined;
    constructionTypeId: number | undefined;
    name: string;
    isDirtyObjectName?: boolean;
}

const requestId = 'uerIsUniqueContractObjectName';

export const useUerIsUniqueContractObjectName = (props: IUseUerIsUniqueContractObjectName) => {
    const dispatch = useDispatch();

    const { contractId, constructionTypeId, name, isDirtyObjectName } = props;

    const variables = useMemo(
        () =>
            !!constructionTypeId && !!name?.length ? { contractId, constructionTypeId, name: name?.trim() } : undefined,
        [constructionTypeId, contractId, name],
    );

    const [
        uerIsUniqueContractObjectNameData,
        uerIsUniqueContractObjectNameStatus,
        uerIsUniqueContractObjectNameError,
        uerIsUniqueContractObjectNameRequest,
    ] = useRequest({
        requestId,
        query: uerIsUniqueContractObjectNameGql,
        withEffect: false,
    });

    const result = useMemo(
        () => uerIsUniqueContractObjectNameData?.uerIsUniqueContractObjectName?.result,
        [uerIsUniqueContractObjectNameData?.uerIsUniqueContractObjectName?.result],
    );

    const debounceRequest = useDebounce(() => {
        if (name && isDirtyObjectName) {
            dispatch(uerIsUniqueContractObjectNameRequest(variables));
        }
    }, 300);

    const [uerIsUniqueContractObjectNameReset] = useResetStoreData({ requestId });

    useEffect(() => {
        if (name?.length) {
            debounceRequest();
        }
    }, [debounceRequest, name?.length]);

    useEffect(
        () => () => {
            uerIsUniqueContractObjectNameReset(undefined);
        },
        [uerIsUniqueContractObjectNameReset],
    );

    return {
        result,
        uerIsUniqueContractObjectNameStatus,
        uerIsUniqueContractObjectNameError,
    };
};
